// TODO: There might be some issues with value that can be array or number.
import React, { useRef, useEffect, useState, useContext, MouseEventHandler } from 'react';
import 'focus-within-polyfill';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import useMeasure from 'react-use-measure';
import ResizeObserver from 'resize-observer-polyfill';
import { SliderContext } from 'components/Slider';
import Thumb from './Thumb';
import Styled from './styles';

const THUMB_NAME = { first: 'first', second: 'second' };

export interface SliderTrackProps extends React.ComponentPropsWithRef<'div'> {
  /** If true, slider will act like ticker. */
  isTicker?: boolean;
  /** Start value for slider. */
  leftValue?: number;
  /** End value for slider. */
  rightValue?: number;
  /**
   * Amount that the value of the input will increment by. By default this is set to 1.
   */
  stepBy?: number;
  /**
   * Callback function to capture the value of a slider. Returns object of shape: ({event, value}):
   *   - event: SyntheticEvent.nativeEvent.
   *   - value: a value that represents the value of the slider.
   */
  onChange?(...args: unknown[]): unknown;
  /**
   * This is the default value of the slider. This will appear on the slider when page loads.
   */
  value: number | number[];
  /**
   * If true, the slider won't be functional, it will be greyed out, and there will be no pointer when hovering.
   */
  disabled?: boolean;
  /**
   * Props only for track.
   */
  trackProps?: {
    onPointerDown?: MouseEventHandler;
    frontTrackProps?: React.HTMLAttributes<'div'>;
    rearTrackProps?: React.HTMLAttributes<'div'>;
    ref?: any
  };
  /**
   * Props only for thumb wrapper element.
   * User could also pass custom props into it.
   *
   * `teardropContent`: Custom teardrop component.
   *
   * `thumbContent`: Custom thumb component.
   *
   * `wrapperProps`: Props which will be passed to thumb wrapper element,
   *
   * `flipIndicatorPosition`: If true, tearDrop will stay visible when slider is ticker mode
   *
   * `alwaysShowTickerTeardrop`: If true, the thumb indicator and tearDrop will be placed on the other side of the track
   */
  thumbProps?: {
    teardropContent?(...args: unknown[]): unknown,
    thumbContent?(...args: unknown[]): unknown,
    wrapperProps?: {},
    flipIndicatorPosition?: boolean,
    alwaysShowTickerTeardrop?: boolean,
    minThumbProps?: any,
    maxThumbProps?: any
  };
  /** Set `true` to render vertical SliderTrack */
  vertical?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** Component to add track component to Slider. All other props will be provided to the wrapper element. */
const SliderTrack = React.forwardRef<HTMLElement, SliderTrackProps>((props: SliderTrackProps, ref) => getContext(({ RTL, theme }) => {
  const {
    value,
    stepBy,
    vertical,
    leftValue,
    rightValue,
    onChange,
    isTicker,
    theme: themeProp,
    disabled,
    trackProps,
    thumbProps,
    ...otherProps
  } = props;
  const { disableAnimation } = useContext(SliderContext);
  const resolvedTheme = _merge({}, theme.SLIDER.track, themeProp);

  const {
    onPointerDown,
    frontTrackProps,
    rearTrackProps,
    ref: passedTrackRef,
    ...restTrackProps
  } = trackProps;
  const { style: selectTrackStyle, ...restSelectTrackProps } = frontTrackProps || {};
  const { style: voidTrackStyle, ...restVoidTrackProps } = rearTrackProps || {};

  const thumbRef = useRef(null);
  const secondThumbRef = useRef(null);

  const currentPos = useRef(0);
  const currentSecondPos = useRef(0);

  const trackPosition = useRef<{ x: number | undefined, y: number | undefined }>({ x: undefined, y: undefined });
  const stepByRatio = useRef(stepBy / Math.abs(leftValue - rightValue));
  const markerDetail = useRef({ markerNumber: 0, markerDistance: 0 });
  const lastChanged = useRef<string>();

  const [showThumb, setShowThumb] = useState(false);

  const [pos, setPos] = useState(0);
  const [secondPos, setSecondPos] = useState(1);

  const [isTickerThumbDragging, setIsTickerThumbDragging] = useState(false);
  const [tickers, setTickers] = useState([]);

  const [trackRef, { width: trackWidth, height: trackHeight }] = useMeasure({ polyfill: ResizeObserver });
  const combinedTrackRef = useCombinedRefs(trackRef, passedTrackRef);

  const getReferenceLength = () => vertical ? trackHeight : trackWidth;
  const isRangeSelection = typeof value !== 'number' && value.length === 2;

  const firstThumbValue: () => number = () => {
    let result:number;
    if (isRangeSelection) {
      const minValue = Math.min(value[0], value[1]);
      const maxValue = Math.max(value[0], value[1]);
      if (vertical) return pos > secondPos ? minValue : maxValue;
      result = secondPos > pos ? minValue : maxValue;
      if (RTL) {
        result = secondPos > pos ? maxValue : minValue;
      }
      return result;
    }
    return value as number;
  };

  const secondThumbValue: () => number = () => {
    if (isRangeSelection) {
      const minValue = Math.min(value[0], value[1]);
      const maxValue = Math.max(value[0], value[1]);
      if (vertical) return pos > secondPos ? maxValue : minValue;
      let retValue:number = secondPos > pos ? maxValue : minValue;
      if (RTL) {
        retValue = secondPos > pos ? minValue : maxValue;
      }
      return retValue;
    }
    return 0;
  };

  const firstThumbReferenceValue = useRef(firstThumbValue());
  const secondThumbReferenceValue = useRef(secondThumbValue());

  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange({
        event,
        value: newValue
      });
    }
  };

  /** calculate percentage of position where thumb should be */
  const calculateRatio = (newPos) => {
    const totalStepNumber = 1 / stepByRatio.current;
    const decimalPart = totalStepNumber - Math.trunc(totalStepNumber);
    const rawPosRatio = newPos / getReferenceLength();
    /** calculate accurate percentage of position  */
    const currentStepNumber = rawPosRatio * totalStepNumber;
    let ratio;
    let rawRatio;
    /** when the track cannot be evenly divided by step (ex: stepBy is 3, min is 0 max is 10. There will be 3.33 steps)
     * When position is at second part of last step range, round it */
    if (decimalPart !== 0 && (currentStepNumber - Math.trunc(totalStepNumber)) > (decimalPart / 2)) {
      ratio = 1;
      rawRatio = 1;
    } else {
      ratio = Math.round(currentStepNumber) * stepByRatio.current;
      rawRatio = !isTicker ? ratio : rawPosRatio;
    }
    return { ratio, rawRatio };
  };

  const calculatePosByValue = (val) => {
    const ratio = Math.abs((val - leftValue) / (rightValue - leftValue));
    const accuratePos = vertical ? (1 - ratio) * getReferenceLength() : ratio * getReferenceLength();
    return accuratePos;
  };

  const calculateValueByPos = (position) => {
    const { ratio } = calculateRatio(position);
    let referenceValue:number = typeof value === 'number' ? value : value[0];
    if (isRangeSelection) {
      referenceValue = position >= pos && position >= secondPos ? value[1] : value[0];
    }
    const stepByDecimalPart = stepBy - Math.trunc(stepBy);
    const precision = stepByDecimalPart > 0 ? `${stepByDecimalPart}`.length - 2 : 0;
    const timeOfTen = 10 ** precision; // turn decimal to integer. Floating number calculation of JS is not accurate.

    if (leftValue < rightValue) {
      const valueDifference = (leftValue + (ratio * Math.abs(rightValue - leftValue))) - referenceValue;
      if (Math.abs(valueDifference) >= stepBy) {
        const timeOfStepBy = parseInt(`${Math.round(Math.abs(valueDifference) / stepBy)}`, 10);
        const valueDelta = (timeOfStepBy * stepBy) * 1;
        const result = Math.round((valueDifference > 0 ? referenceValue + valueDelta : referenceValue - valueDelta) * timeOfTen) / timeOfTen;
        return result;
      }
      return referenceValue;
    }

    const rawValue = leftValue - (ratio * Math.abs(rightValue - leftValue));
    const result = Math.round(rawValue * timeOfTen) / timeOfTen;
    return result;
  };


  const calculateTrackTransform = () => {
    let frontTrackRatio;
    let rearTrackRatio;
    if (isRangeSelection) {
      const { rawRatio: ratioFront } = !isTicker ? calculateRatio(calculatePosByValue(value[0])) : calculateRatio(Math.min(pos, secondPos));
      const { rawRatio: ratioRear } = !isTicker ? calculateRatio(calculatePosByValue(value[1])) : calculateRatio(Math.max(pos, secondPos));
      frontTrackRatio = ratioFront;
      rearTrackRatio = ratioRear;
    } else {
      const { rawRatio } = !isTicker ? calculateRatio(calculatePosByValue(value)) : calculateRatio(pos);
      frontTrackRatio = rawRatio;
      rearTrackRatio = rawRatio;
    }
    return { frontTrackRatio, rearTrackRatio };
  };


  const setPosition = (position) => {
    currentPos.current = position;
    setPos(position);
  };

  const setSecondPosition = (position) => {
    currentSecondPos.current = position;
    setSecondPos(position);
  };

  const setAccuratePos = () => {
    const referenceValue:number = typeof value === 'number' ? value : value[0];
    const accuratePos =
      currentPos.current === getReferenceLength()
        ? currentPos.current
        : (Math.abs(referenceValue - leftValue) / Math.abs(rightValue - leftValue)) * getReferenceLength();
    setPosition(accuratePos);
  };

  const calculateMarkers = () => {
    /** when slider is ticker and stepBy is changed, generate tickers */
    if (isTicker) {
      const markerNumber = Math.floor((1 / stepByRatio.current) + 1);
      const markerDistance = getReferenceLength() * stepByRatio.current;
      if (
        markerNumber !== markerDetail.current.markerNumber ||
        markerDistance !== markerDetail.current.markerDistance
      ) {
        markerDetail.current = {
          markerNumber,
          markerDistance
        };
        const iterators = Array.from(Array(markerNumber).keys());
        const newMarkers = iterators.map((i) => {
          const position = (i * markerDistance) - 2;
          return <Styled.Marker vertical={vertical} isLeftEnd={i === 0} isRightEnd={i === iterators.length - 1} key={`marker_${i}`} style={vertical ? { top: position } : { left: position }} />;
        });
        setTickers(newMarkers);
      }
    }
  };

  /** when the width of slider is changed, recalculate the position for thumb and tickers */
  useEffect(() => {
    const rect = combinedTrackRef.current.getBoundingClientRect();
    const length = vertical ? trackHeight : trackWidth;
    if (length !== getReferenceLength()) {
      trackPosition.current = rect;
      if (isTicker) {
        calculateMarkers();
      }
      setAccuratePos();
    }
    trackPosition.current = rect;
  }, [trackHeight, trackWidth]);

  const normalizeValue = (val) => {
    const largerValue = Math.max(leftValue, rightValue);
    const smallerValue = Math.min(leftValue, rightValue);
    let res = val;
    if (val >= largerValue) {
      res = largerValue;
    } else if (val <= smallerValue) {
      res = smallerValue;
    }
    return res;
  };

  const calculatePositions = () => {
    let normalizedValue;
    let firstPosition;
    let secondPosition;
    if (isRangeSelection) {
      // First time render, initialize positions from two values of value prop.
      if (!showThumb || !lastChanged.current) {
        normalizedValue = [normalizeValue(value[0]), normalizeValue(value[1])];
        firstPosition = calculatePosByValue(normalizedValue[0]);
        secondPosition = calculatePosByValue(normalizedValue[1]);
      } else if (showThumb) {
        // When value range is changed, calculate the position based on which thumb just moved
        if (lastChanged.current === THUMB_NAME.first) {
          secondPosition = secondPos;
          // find the changed value and update position for first thumb
          if (value[0] === value[1]) {
            firstPosition = calculatePosByValue(value[0]);
          }
          value.forEach((val) => {
            if (val !== secondThumbReferenceValue.current) {
              firstPosition = calculatePosByValue(val);
            }
          });
        } else {
          firstPosition = pos;
          // find the changed value and update position for second thumb
          if (value[0] === value[1]) {
            secondPosition = calculatePosByValue(value[0]);
          }
          value.forEach((val) => {
            if (val !== firstThumbReferenceValue.current) {
              secondPosition = calculatePosByValue(val);
            }
          });
        }
      }
    } else {
      normalizedValue = normalizeValue(value);
      firstPosition = calculatePosByValue(normalizedValue);
    }

    return { firstPosition, secondPosition };
  };

  // reset lastChanged when RTL changes to update all position calculations
  useEffect(() => {
    lastChanged.current = undefined;
  }, [RTL]);

  useEffect(() => {
    if (combinedTrackRef.current && trackWidth !== 0 && trackHeight !== 0) {
      const { firstPosition, secondPosition } = calculatePositions();
      if (!Number.isNaN(firstPosition)) {
        setPosition(firstPosition);
        secondPosition !== null && setSecondPosition(secondPosition);
        setShowThumb(true);
        if (lastChanged.current === THUMB_NAME.second) {
          secondThumbRef.current && secondThumbRef.current.focus();
          firstThumbReferenceValue.current = firstThumbValue();
          secondThumbReferenceValue.current = secondThumbValue();
        } else {
          thumbRef.current && thumbRef.current.focus();
        }
      }
    }
  }, [value, combinedTrackRef.current, trackWidth, trackHeight, RTL]);

  useEffect(() => {
    firstThumbReferenceValue.current = firstThumbValue();
    secondThumbReferenceValue.current = secondThumbValue();
  }, [pos, secondPos]);

  useEffect(() => {
    if (combinedTrackRef.current) {
      stepByRatio.current = stepBy / Math.abs(leftValue - rightValue);
      const { firstPosition, secondPosition } = calculatePositions();
      setPosition(firstPosition);
      secondPosition !== null && setSecondPosition(secondPosition);
    }
    if (isTicker) {
      calculateMarkers();
    }
  }, [leftValue, rightValue, combinedTrackRef.current, RTL]);

  /** when stepBy is changed, update position of tickers and thumb */
  useEffect(() => {
    stepByRatio.current = stepBy / Math.abs(rightValue - leftValue);
    /** when stepBy is changed, update position */
    const { firstPosition, secondPosition } = calculatePositions();

    setPosition(firstPosition);
    secondPosition !== null && setSecondPosition(secondPosition);
    if (isTicker) {
      calculateMarkers();
    }
  }, [stepBy, isTicker, RTL]);

  const validateTrackClickValue = (newValue) => {
    let returnValue = newValue;
    if (isRangeSelection) {
      const firstDividend = Math.abs(value[0] - newValue);
      const secondDividend = Math.abs(value[1] - newValue);
      if (firstDividend >= secondDividend) {
        returnValue = [value[0], newValue];
        const isFirstPosLarger = pos > secondPos;
        switch (isFirstPosLarger) {
          case false:
            lastChanged.current = THUMB_NAME.second;
            break;
          case true:
            lastChanged.current = THUMB_NAME.first;
            break;
          default:
            lastChanged.current = THUMB_NAME.first;
        }
      } else {
        returnValue = [newValue, value[1]];
        const isFirstPosLarger = pos > secondPos;
        switch (isFirstPosLarger) {
          case false:
            lastChanged.current = THUMB_NAME.first;
            break;
          case true:
            lastChanged.current = THUMB_NAME.second;
            break;
          default:
            lastChanged.current = THUMB_NAME.first;
        }
      }
    }

    if (lastChanged.current === THUMB_NAME.first) {
      firstThumbReferenceValue.current = newValue;
    } else {
      secondThumbReferenceValue.current = newValue;
    }
    return returnValue;
  };

  /** when click on track, adjust thumb position */
  const onPointerDownTrack = (e) => {
    if (!disabled && trackPosition.current.y !== undefined && trackPosition.current.x !== undefined) {
      onPointerDown && onPointerDown(e);
      const newPos = vertical ? e.clientY - trackPosition.current.y : e.clientX - trackPosition.current.x;
      const { ratio } = calculateRatio(newPos);
      const newValue = calculateValueByPos(ratio * getReferenceLength());
      const validatedValue = validateTrackClickValue(newValue);
      handleChange(e.nativeEvent, validatedValue);
      e.preventDefault();
    }
  };

  const ascSortCompareFunc = (a, b) => {
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
  };

  const handleFirstThumbChange = (event, newValue) => {
    lastChanged.current = THUMB_NAME.first;
    let returnValue = newValue;
    if (isRangeSelection) {
      returnValue = [returnValue, secondThumbReferenceValue.current].sort(ascSortCompareFunc);
    }
    handleChange(event, returnValue);
  };

  const handleSecondThumbChange = (event, newValue) => {
    lastChanged.current = THUMB_NAME.second;
    let returnValue = newValue;
    if (isRangeSelection) {
      returnValue = [returnValue, firstThumbReferenceValue.current].sort(ascSortCompareFunc);
    }
    handleChange(event, returnValue);
  };

  const getFrontTrackScale = () => {
    const { frontTrackRatio } = calculateTrackTransform();
    if (vertical) {
      return `scaleY(${1 - frontTrackRatio})`;
    }
    return RTL ? `scaleX(${1 - frontTrackRatio})` : `scaleX(${frontTrackRatio})`;
  };

  const getRearTrackScale = () => {
    const { rearTrackRatio } = calculateTrackTransform();
    if (vertical) {
      return `scaleY(${rearTrackRatio})`;
    }
    return RTL ? `scaleX(${rearTrackRatio})` : `scaleX(${1 - rearTrackRatio})`;
  };

  const { minThumbProps, maxThumbProps, ...restOfThumbProps } = thumbProps;

  const commonThumbProps = {
    vertical,
    trackPosition: trackPosition.current,
    calculateRatio,
    normalizeValue,
    calculatePosByValue,
    getIsTickerThumbDragging: setIsTickerThumbDragging,
    calculateValueByPos,
    calculateTrackTransform,
    stepBy,
    isTicker,
    disabled,
    isRangeSelection,
    ...restOfThumbProps
  };

  return (
    <Styled.TrackWrapper onPointerDown={onPointerDownTrack} vertical={vertical} {...otherProps} ref={ref}>
      <Styled.Track
        isRangeSelection={isRangeSelection}
        disabled={disabled}
        $theme={resolvedTheme}
        ref={combinedTrackRef}
        vertical={vertical}
        $disableAnimation={disableAnimation}
        {...restTrackProps}
      >
        <Styled.FrontTrack
          $rtl={RTL}
          isRangeSelection={isRangeSelection}
          $theme={resolvedTheme}
          vertical={vertical}
          isTickerThumbDragging={isTickerThumbDragging}
          style={{ transform: getFrontTrackScale(), ...selectTrackStyle }}
          disabled={disabled}
          $disableAnimation={disableAnimation}
          {...restSelectTrackProps}
        />
        <Styled.RearTrack
          $rtl={RTL}
          $theme={resolvedTheme}
          vertical={vertical}
          isTickerThumbDragging={isTickerThumbDragging}
          style={{ transform: getRearTrackScale(), ...voidTrackStyle }}
          disabled={disabled}
          $disableAnimation={disableAnimation}
          {...restVoidTrackProps}
        />

        {isTicker && tickers}
        {showThumb && <Thumb
          wrapperProps={{ id: 'firstThumb' }}
          ref={thumbRef}
          pos={pos}
          value={firstThumbValue()}
          onChange={handleFirstThumbChange}
          setPosition={setPosition}
          internalThumbProps={minThumbProps}
          {...commonThumbProps}
        />}

        {showThumb && isRangeSelection && <Thumb
          ref={secondThumbRef}
          pos={secondPos}
          value={secondThumbValue()}
          onChange={handleSecondThumbChange}
          setPosition={setSecondPosition}
          internalThumbProps={maxThumbProps}
          {...commonThumbProps}
        />}
      </Styled.Track>
    </Styled.TrackWrapper>
  );
}));

SliderTrack.displayName = 'SliderTrack';

SliderTrack.defaultProps = {
  isTicker: false,
  leftValue: 0,
  rightValue: 10,
  stepBy: 1,
  onChange: null,
  disabled: false,
  trackProps: {},
  thumbProps: {},
  vertical: false,
  theme: {}
};

export default SliderTrack;
